import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  DollarSign, Clock, Users, Wrench, TrendingUp, 
  Calculator, Printer, Building2, Percent
} from "lucide-react";

/**
 * KnowYourNumbersBreakdown Component
 * Displays comprehensive cost breakdown based on company pricing settings
 */
export function KnowYourNumbersBreakdown({ 
  timeEntries = [], 
  materialUsage = [], 
  subcontractorCost = 0,
  overheadSettings = {},
  soldPrice = 0,
  showDetails = true,
  compact = false,
  className = ""
}) {
  // Extract settings with defaults
  const laborBurdenRate = overheadSettings.labor_burden_rate || 35;
  const overheadPerHour = overheadSettings.overhead_per_hour || 0;
  const profitMargin = overheadSettings.target_profit_margin || 20;
  
  // Calculate totals from time entries
  const totalHours = timeEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
  const totalBaseLabor = timeEntries.reduce((sum, e) => {
    const hours = e.hours || 0;
    const rate = e.hourly_rate || 25;
    return sum + (hours * rate);
  }, 0);
  
  // Calculate labor burden cost (taxes, insurance, benefits)
  const laborBurdenCost = totalBaseLabor * (laborBurdenRate / 100);
  const burdenedLaborCost = totalBaseLabor + laborBurdenCost;
  
  // Calculate overhead recovery
  const overheadRecovery = totalHours * overheadPerHour;
  
  // Calculate material costs
  const totalMaterialCost = materialUsage.reduce((sum, m) => 
    sum + ((m.quantity || 0) * (m.unit_cost || 0)), 0);

  // Subcontractor cost — passed in from the parent as a pre-summed
  // number (parent pulls from full-details.actual_subcontractor_cost or
  // sums sub_expenses[].amount). Folded into break-even so True Profit
  // reflects the real cost of the job, not just crew + materials. Feb
  // 2026 bugfix.
  const totalSubcontractorCost = Number(subcontractorCost) || 0;
  
  // Calculate subtotal before profit
  const subtotalBeforeProfit = burdenedLaborCost + overheadRecovery + totalMaterialCost + totalSubcontractorCost;
  
  // Calculate profit
  const targetProfit = subtotalBeforeProfit * (profitMargin / 100);
  
  // Calculate total with profit
  const totalWithProfit = subtotalBeforeProfit + targetProfit;
  
  // === SOLD PRICE COMPARISON =============================================
  // When the admin has set a sold_price on the job, score it against the
  // actuals-derived break-even (labor+burden+overhead+materials). This is
  // the TRUE profit picture: what the customer paid minus what the job
  // actually cost. Distinct from the target/expected math above.
  const soldPriceNum = Number(soldPrice) || 0;
  const actualProfit = soldPriceNum > 0 ? soldPriceNum - subtotalBeforeProfit : 0;
  const actualMarginPct = soldPriceNum > 0 ? (actualProfit / soldPriceNum) * 100 : 0;
  const profitVsTarget = soldPriceNum > 0 ? actualProfit - targetProfit : 0;
  const soldPriceStatus =
    soldPriceNum === 0 ? "none"
      : actualProfit < 0 ? "loss"
        : actualMarginPct >= profitMargin ? "healthy"
          : actualMarginPct >= profitMargin - 5 ? "borderline"
            : "weak";
  
  // Calculate per-hour metrics
  const avgHourlyRate = totalHours > 0 ? totalBaseLabor / totalHours : 0;
  const burdenedRate = avgHourlyRate * (1 + laborBurdenRate / 100);
  const fullyLoadedRate = burdenedRate + overheadPerHour;
  const rateWithProfit = fullyLoadedRate * (1 + profitMargin / 100);

  if (compact) {
    return (
      <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 ${className}`}>
        <div className="bg-zinc-800 rounded-lg p-3">
          <p className="text-xs text-zinc-400 uppercase">Base Labor</p>
          <p className="text-lg font-bold text-white">${totalBaseLabor.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
        </div>
        <div className="bg-zinc-800 rounded-lg p-3">
          <p className="text-xs text-zinc-400 uppercase">+ Burden ({laborBurdenRate}%)</p>
          <p className="text-lg font-bold text-blue-400">${laborBurdenCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
        </div>
        <div className="bg-zinc-800 rounded-lg p-3">
          <p className="text-xs text-zinc-400 uppercase">+ Overhead</p>
          <p className="text-lg font-bold text-purple-400">${overheadRecovery.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
        </div>
        <div className="bg-zinc-800 rounded-lg p-3">
          <p className="text-xs text-zinc-400 uppercase">+ Materials</p>
          <p className="text-lg font-bold text-orange-400">${totalMaterialCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
        </div>
        <div className="bg-zinc-800 rounded-lg p-3 col-span-2">
          <p className="text-xs text-zinc-400 uppercase">Subtotal (Break Even)</p>
          <p className="text-lg font-bold text-zinc-200">${subtotalBeforeProfit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
        </div>
        <div className="bg-green-900/30 border border-green-700/50 rounded-lg p-3 col-span-2">
          <p className="text-xs text-green-400 uppercase">+ Profit ({profitMargin}%)</p>
          <p className="text-xl font-bold text-green-400">${totalWithProfit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
        </div>
      </div>
    );
  }

  return (
    <Card className={`bg-zinc-900 border-zinc-700 ${className}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-heading uppercase flex items-center gap-2">
          <Calculator className="w-5 h-5 text-primary" />
          Know Your Numbers
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary Stats Row */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-zinc-800 rounded-lg p-3 text-center">
            <Clock className="w-5 h-5 text-blue-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-white">{totalHours.toFixed(1)}</p>
            <p className="text-xs text-zinc-400">Total Hours</p>
          </div>
          <div className="bg-zinc-800 rounded-lg p-3 text-center">
            <Users className="w-5 h-5 text-purple-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-white">{new Set(timeEntries.map(e => e.crew_member)).size}</p>
            <p className="text-xs text-zinc-400">Crew Members</p>
          </div>
          <div className="bg-zinc-800 rounded-lg p-3 text-center">
            <Wrench className="w-5 h-5 text-orange-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-white">{materialUsage.length}</p>
            <p className="text-xs text-zinc-400">Materials</p>
          </div>
          <div className="bg-zinc-800 rounded-lg p-3 text-center">
            <DollarSign className="w-5 h-5 text-green-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-green-400">${totalWithProfit.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</p>
            <p className="text-xs text-zinc-400">Total Value</p>
          </div>
        </div>

        {showDetails && (
          <>
            {/* Cost Breakdown */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-zinc-300 uppercase tracking-wide">Cost Breakdown</h4>
              
              {/* Labor Section */}
              <div className="bg-zinc-800/50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-zinc-300 flex items-center gap-2">
                    <Users className="w-4 h-4" /> Base Labor Cost
                  </span>
                  <span className="font-mono text-white">${totalBaseLabor.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-blue-400 ml-6">+ Labor Burden ({laborBurdenRate}%)</span>
                  <span className="font-mono text-blue-400">+${laborBurdenCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center pt-1 border-t border-zinc-700">
                  <span className="text-zinc-200 font-medium">Burdened Labor Cost</span>
                  <span className="font-mono font-bold text-white">${burdenedLaborCost.toFixed(2)}</span>
                </div>
              </div>

              {/* Overhead Section */}
              <div className="bg-zinc-800/50 rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <span className="text-purple-400 flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> Overhead Recovery
                    <Badge variant="outline" className="text-xs border-purple-500/50 text-purple-400">
                      ${overheadPerHour.toFixed(2)}/hr × {totalHours.toFixed(1)}hrs
                    </Badge>
                  </span>
                  <span className="font-mono text-purple-400">+${overheadRecovery.toFixed(2)}</span>
                </div>
              </div>

              {/* Materials Section */}
              <div className="bg-zinc-800/50 rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <span className="text-orange-400 flex items-center gap-2">
                    <Wrench className="w-4 h-4" /> Materials Cost
                    <Badge variant="outline" className="text-xs border-orange-500/50 text-orange-400">
                      {materialUsage.length} items
                    </Badge>
                  </span>
                  <span className="font-mono text-orange-400">+${totalMaterialCost.toFixed(2)}</span>
                </div>
              </div>

              {/* Subcontractors Section — only render if any sub cost is
                  present so the panel stays clean for jobs without subs. */}
              {totalSubcontractorCost > 0 && (
                <div className="bg-zinc-800/50 rounded-lg p-3" data-testid="kyn-sub-cost-row">
                  <div className="flex justify-between items-center">
                    <span className="text-amber-400 flex items-center gap-2">
                      <Users className="w-4 h-4" /> Subcontractor Charges
                    </span>
                    <span className="font-mono text-amber-400">+${totalSubcontractorCost.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Subtotal */}
              <div className="bg-zinc-700/50 rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <span className="text-zinc-200 font-semibold">Subtotal (Break Even Point)</span>
                  <span className="font-mono font-bold text-lg text-white">${subtotalBeforeProfit.toFixed(2)}</span>
                </div>
                <p className="text-xs text-zinc-500 mt-1">This is your true cost - charge less and you lose money</p>
              </div>

              {/* Profit Section */}
              <div className="bg-green-900/30 border border-green-700/50 rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <span className="text-green-400 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" /> Target Profit ({profitMargin}%)
                  </span>
                  <span className="font-mono text-green-400">+${targetProfit.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-green-700/50">
                  <span className="text-green-300 font-bold text-lg">TOTAL VALUE</span>
                  <span className="font-mono font-bold text-2xl text-green-400">${totalWithProfit.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Sold Price vs Actuals comparison — surfaces only when the
                admin has set a sold_price on the job. This is the TRUE
                profit picture: what the customer paid minus what the job
                actually cost in time + materials + burden + overhead. */}
            {soldPriceNum > 0 && (
              <div
                className={`rounded-lg p-3 border ${
                  soldPriceStatus === "loss"
                    ? "bg-red-900/30 border-red-700/50"
                    : soldPriceStatus === "weak"
                    ? "bg-orange-900/30 border-orange-700/50"
                    : soldPriceStatus === "borderline"
                    ? "bg-amber-900/30 border-amber-700/50"
                    : "bg-emerald-900/30 border-emerald-700/50"
                }`}
                data-testid="kyn-sold-price-comparison"
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Sold Price vs Actuals
                  </span>
                  <Badge
                    variant="outline"
                    className={`text-xs ${
                      soldPriceStatus === "loss"
                        ? "border-red-500 text-red-300"
                        : soldPriceStatus === "weak"
                        ? "border-orange-500 text-orange-300"
                        : soldPriceStatus === "borderline"
                        ? "border-amber-500 text-amber-300"
                        : "border-emerald-500 text-emerald-300"
                    }`}
                  >
                    {soldPriceStatus === "loss"
                      ? "LOSS"
                      : soldPriceStatus === "weak"
                      ? "Below Target"
                      : soldPriceStatus === "borderline"
                      ? "Borderline"
                      : "Healthy"}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-zinc-400">Sold Price</p>
                    <p className="font-mono font-bold text-white">${soldPriceNum.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-400">Actual Cost (Break-even)</p>
                    <p className="font-mono font-bold text-zinc-200">${subtotalBeforeProfit.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-400">Actual Profit</p>
                    <p className={`font-mono font-bold ${actualProfit < 0 ? "text-red-400" : "text-emerald-400"}`}>
                      ${actualProfit.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-400">Actual Margin</p>
                    <p className={`font-mono font-bold ${actualMarginPct >= profitMargin ? "text-emerald-400" : actualMarginPct < 0 ? "text-red-400" : "text-amber-400"}`}>
                      {actualMarginPct.toFixed(1)}%
                    </p>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-current/20 text-xs text-zinc-400 flex items-center justify-between">
                  <span>vs Target ({profitMargin}%): <span className={`font-semibold ${profitVsTarget >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {profitVsTarget >= 0 ? "+" : ""}${profitVsTarget.toFixed(2)}
                  </span></span>
                  {actualProfit < 0 && (
                    <span className="text-red-400 font-semibold">Cost exceeds sold price by ${Math.abs(actualProfit).toFixed(2)}</span>
                  )}
                </div>
              </div>
            )}

            {/* Rate Analysis */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-zinc-300 uppercase tracking-wide">Hourly Rate Analysis</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="bg-zinc-800 rounded p-2 text-center">
                  <p className="text-xs text-zinc-400">Avg Base Rate</p>
                  <p className="text-lg font-bold text-white">${avgHourlyRate.toFixed(2)}/hr</p>
                </div>
                <div className="bg-zinc-800 rounded p-2 text-center">
                  <p className="text-xs text-blue-400">Burdened Rate</p>
                  <p className="text-lg font-bold text-blue-400">${burdenedRate.toFixed(2)}/hr</p>
                </div>
                <div className="bg-zinc-800 rounded p-2 text-center">
                  <p className="text-xs text-purple-400">Fully Loaded</p>
                  <p className="text-lg font-bold text-purple-400">${fullyLoadedRate.toFixed(2)}/hr</p>
                </div>
                <div className="bg-green-900/30 rounded p-2 text-center border border-green-700/30">
                  <p className="text-xs text-green-400">Bill Rate</p>
                  <p className="text-lg font-bold text-green-400">${rateWithProfit.toFixed(2)}/hr</p>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * CostSummaryRow Component - For inline cost display in tables
 */
export function CostSummaryRow({ 
  hours = 0, 
  hourlyRate = 25, 
  laborBurdenRate = 35, 
  overheadPerHour = 0, 
  profitMargin = 20,
  materialCost = 0
}) {
  const baseLabor = hours * hourlyRate;
  const laborBurden = baseLabor * (laborBurdenRate / 100);
  const burdenedLabor = baseLabor + laborBurden;
  const overhead = hours * overheadPerHour;
  const subtotal = burdenedLabor + overhead + materialCost;
  const profit = subtotal * (profitMargin / 100);
  const total = subtotal + profit;
  
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-zinc-400">${baseLabor.toFixed(0)}</span>
      <span className="text-blue-400">+${laborBurden.toFixed(0)}</span>
      <span className="text-purple-400">+${overhead.toFixed(0)}</span>
      {materialCost > 0 && <span className="text-orange-400">+${materialCost.toFixed(0)}</span>}
      <span className="text-zinc-500">=</span>
      <span className="text-green-400 font-bold">${total.toFixed(0)}</span>
    </div>
  );
}

/**
 * PrintableJobReport Component - For generating printable reports
 */
export function PrintableJobReport({ 
  customer, 
  jobs = [],
  timeEntries = [], 
  materialUsage = [], 
  overheadSettings = {},
  companyInfo = {}
}) {
  const laborBurdenRate = overheadSettings.labor_burden_rate || 35;
  const overheadPerHour = overheadSettings.overhead_per_hour || 0;
  const profitMargin = overheadSettings.target_profit_margin || 20;
  
  // Group entries by job
  const entriesByJob = jobs.reduce((acc, job) => {
    acc[job.id] = {
      job,
      timeEntries: timeEntries.filter(e => e.job_id === job.id),
      materials: materialUsage.filter(m => m.job_id === job.id)
    };
    return acc;
  }, {});

  const calculateJobCosts = (jobTimeEntries, jobMaterials) => {
    const totalHours = jobTimeEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
    const baseLabor = jobTimeEntries.reduce((sum, e) => sum + ((e.hours || 0) * (e.hourly_rate || 25)), 0);
    const laborBurden = baseLabor * (laborBurdenRate / 100);
    const overhead = totalHours * overheadPerHour;
    const materialCost = jobMaterials.reduce((sum, m) => sum + ((m.quantity || 0) * (m.unit_cost || 0)), 0);
    const subtotal = baseLabor + laborBurden + overhead + materialCost;
    const profit = subtotal * (profitMargin / 100);
    return { totalHours, baseLabor, laborBurden, overhead, materialCost, subtotal, profit, total: subtotal + profit };
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="print-report">
      {/* Print Button (hidden when printing) */}
      <div className="print:hidden mb-4 flex justify-end">
        <Button onClick={handlePrint} className="bg-primary text-black font-bold">
          <Printer className="w-4 h-4 mr-2" /> Print Report
        </Button>
      </div>

      {/* Report Content */}
      <div className="bg-white text-black p-6 rounded-lg print:p-0 print:shadow-none">
        {/* Header */}
        <div className="border-b-2 border-black pb-4 mb-4">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold">{companyInfo.name || 'Company Name'}</h1>
              <p className="text-sm text-gray-600">{companyInfo.address}</p>
              <p className="text-sm text-gray-600">{companyInfo.phone} | {companyInfo.email}</p>
            </div>
            <div className="text-right">
              <h2 className="text-xl font-bold">Work Report</h2>
              <p className="text-sm text-gray-600">Generated: {new Date().toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        {/* Customer Info */}
        <div className="mb-6 bg-gray-100 p-4 rounded">
          <h3 className="font-bold text-lg mb-2">Customer Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="font-semibold">{customer?.name}</p>
              <p className="text-sm">{customer?.address}</p>
              <p className="text-sm">{customer?.city}, {customer?.state} {customer?.zip}</p>
            </div>
            <div className="text-right">
              <p className="text-sm">{customer?.email}</p>
              <p className="text-sm">{customer?.phone}</p>
            </div>
          </div>
        </div>

        {/* Jobs Breakdown */}
        {Object.values(entriesByJob).map(({ job, timeEntries: jobEntries, materials: jobMaterials }) => {
          const costs = calculateJobCosts(jobEntries, jobMaterials);
          
          return (
            <div key={job.id} className="mb-6 border border-gray-300 rounded p-4">
              <h4 className="font-bold text-lg border-b pb-2 mb-3">{job.title}</h4>
              
              {/* Time Entries */}
              {jobEntries.length > 0 && (
                <div className="mb-4">
                  <h5 className="font-semibold text-sm uppercase text-gray-600 mb-2">Time Entries</h5>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Crew Member</th>
                        <th className="text-right p-2">Hours</th>
                        <th className="text-right p-2">Rate</th>
                        <th className="text-right p-2">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobEntries.map((entry, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="p-2">{new Date(entry.clock_in).toLocaleDateString()}</td>
                          <td className="p-2">{entry.crew_member}</td>
                          <td className="p-2 text-right">{(entry.hours || 0).toFixed(2)}</td>
                          <td className="p-2 text-right">${entry.hourly_rate || 25}/hr</td>
                          <td className="p-2 text-right">${((entry.hours || 0) * (entry.hourly_rate || 25)).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Materials */}
              {jobMaterials.length > 0 && (
                <div className="mb-4">
                  <h5 className="font-semibold text-sm uppercase text-gray-600 mb-2">Materials Used</h5>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left p-2">Material</th>
                        <th className="text-right p-2">Qty</th>
                        <th className="text-right p-2">Unit Cost</th>
                        <th className="text-right p-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobMaterials.map((mat, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="p-2">{mat.material_name}</td>
                          <td className="p-2 text-right">{mat.quantity}</td>
                          <td className="p-2 text-right">${(mat.unit_cost || 0).toFixed(2)}</td>
                          <td className="p-2 text-right">${((mat.quantity || 0) * (mat.unit_cost || 0)).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Job Cost Summary */}
              <div className="bg-gray-50 p-3 rounded">
                <h5 className="font-semibold text-sm uppercase text-gray-600 mb-2">Cost Breakdown</h5>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between">
                    <span>Base Labor:</span>
                    <span>${costs.baseLabor.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-blue-600">
                    <span>+ Labor Burden ({laborBurdenRate}%):</span>
                    <span>${costs.laborBurden.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-purple-600">
                    <span>+ Overhead (${overheadPerHour}/hr):</span>
                    <span>${costs.overhead.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-orange-600">
                    <span>+ Materials:</span>
                    <span>${costs.materialCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-semibold border-t pt-1">
                    <span>Subtotal:</span>
                    <span>${costs.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>+ Profit ({profitMargin}%):</span>
                    <span>${costs.profit.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg col-span-2 border-t pt-1">
                    <span>Job Total:</span>
                    <span className="text-green-700">${costs.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Grand Total */}
        <div className="mt-6 bg-black text-white p-4 rounded">
          <div className="flex justify-between items-center">
            <span className="text-xl font-bold">GRAND TOTAL</span>
            <span className="text-2xl font-bold">
              ${Object.values(entriesByJob).reduce((sum, { timeEntries: te, materials: m }) => {
                return sum + calculateJobCosts(te, m).total;
              }, 0).toFixed(2)}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t text-center text-sm text-gray-500">
          <p>Thank you for your business!</p>
          <p>Report generated by CrewSync Pro</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-report, .print-report * { visibility: visible; }
          .print-report { position: absolute; left: 0; top: 0; width: 100%; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}

export default KnowYourNumbersBreakdown;
